// IndexedDB-backed key/value store for E2E state (ТП-3). Holds the crypto identity,
// prekeys and per-peer Double-Ratchet session state — now ENCRYPTED AT REST: every
// value is AES-256-GCM encrypted with a per-DB, NON-EXTRACTABLE WebCrypto key kept in
// the same store. A raw IndexedDB dump or device backup therefore yields only
// ciphertext plus an opaque key handle whose bytes cannot be exported by script
// (КР-8). NOTE: this does NOT defend a fully compromised device that can run code in
// this origin — for that, add a PIN/passkey unlock (a later step).
//
// One DB per logged-in user (namespaced by uid) so switching accounts can't mix key
// material. Reads transparently accept LEGACY plaintext values (pre-encryption) and
// they get re-encrypted on the next write — no migration break.

const STORE = 'kv';
const WKEY = '__wrap__'; // holds the non-extractable AES-GCM CryptoKey
const subtle = (typeof globalThis !== 'undefined' && globalThis.crypto && globalThis.crypto.subtle) || null;

function openDB(uid) {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open('nexus-e2e:' + uid, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// Serialize a value (object with nested Uint8Arrays) to bytes and back. Crypto-state
// Uint8Arrays are all small (≤64B keys, 32B skipped-message keys), so fromCharCode is safe.
function ser(obj) {
  const json = JSON.stringify(obj, (k, v) =>
    (v instanceof Uint8Array) ? { __u8: btoa(String.fromCharCode.apply(null, v)) } : v);
  return new TextEncoder().encode(json);
}
function deser(bytes) {
  const json = new TextDecoder().decode(bytes);
  return JSON.parse(json, (k, v) =>
    (v && typeof v === 'object' && typeof v.__u8 === 'string')
      ? Uint8Array.from(atob(v.__u8), (c) => c.charCodeAt(0)) : v);
}

export async function createIdbStore(uid) {
  const db = await openDB(uid);
  const tx = (mode) => db.transaction(STORE, mode).objectStore(STORE);
  const wrap = (req) => new Promise((res, rej) => { req.onsuccess = () => res(req.result); req.onerror = () => rej(req.error); });

  // Get-or-create the per-DB, non-extractable wrapping key.
  let wrapKey = null;
  if (subtle) {
    try {
      wrapKey = await wrap(tx('readonly').get(WKEY));
      if (!wrapKey) {
        wrapKey = await subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
        // generateKey is fully awaited BEFORE opening the write tx (an IndexedDB tx
        // auto-closes if you await between creating it and using it).
        await wrap(tx('readwrite').put(wrapKey, WKEY));
      }
    } catch (e) { wrapKey = null; }
  }

  // FAIL CLOSED (audit, 2nd tier): if we cannot obtain the at-rest wrapping key —
  // WebCrypto unavailable, or the key read/generate failed — we must NOT write E2E
  // secrets to disk in the clear (the old behaviour). Fall back to an IN-MEMORY store:
  // messaging keeps working for this session, nothing sensitive is persisted, and the
  // session state is simply re-established (re-handshake) on the next reload. Cloning
  // on get/set mirrors IndexedDB's structured-clone semantics so callers can't mutate
  // stored state in place.
  if (!wrapKey) {
    try {
      console.warn('[Nexus E2E] at-rest encryption unavailable — keeping key material in memory only (no plaintext on disk)');
    } catch (e) { /* ignore */ }
    const mem = new Map();
    return {
      async get(key) { return mem.has(key) ? structuredClone(mem.get(key)) : undefined; },
      async set(key, value) { mem.set(key, structuredClone(value)); },
      async del(key) { mem.delete(key); },
      async list(prefix) {
        const out = [];
        for (const [k, v] of mem) {
          if (typeof k === 'string' && k.startsWith(prefix)) {
            out.push({ key: k, value: structuredClone(v) });
          }
        }
        return out;
      },
    };
  }

  async function enc(value) {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = new Uint8Array(await subtle.encrypt({ name: 'AES-GCM', iv }, wrapKey, ser(value)));
    return { __enc: 1, iv, ct };
  }
  async function dec(stored) {
    if (!stored || stored.__enc !== 1) return stored; // legacy plaintext -> re-encrypted on next write
    const pt = new Uint8Array(await subtle.decrypt({ name: 'AES-GCM', iv: stored.iv }, wrapKey, stored.ct));
    return deser(pt);
  }

  return {
    async get(key) { return dec(await wrap(tx('readonly').get(key))); },
    // Encrypt BEFORE opening the tx (see the tx-timing note above).
    async set(key, value) { const v = await enc(value); return wrap(tx('readwrite').put(v, key)); },
    async del(key) { return wrap(tx('readwrite').delete(key)); },
    // All entries whose key starts with `prefix` — used to preload sessions.
    async list(prefix) {
      const store = tx('readonly');
      const keys = await wrap(store.getAllKeys());
      const vals = await wrap(store.getAll());
      const out = [];
      for (let i = 0; i < keys.length; i++) {
        if (typeof keys[i] === 'string' && keys[i] !== WKEY && keys[i].startsWith(prefix)) {
          out.push({ key: keys[i], value: await dec(vals[i]) });
        }
      }
      return out;
    },
  };
}
